import { useReveal } from '../../../hooks/useReveal';

const cards = [
  {
    icon: 'ri-handshake-line',
    title: 'No Pressure, No Commitment',
    desc: "A simple strategy call to see if we're a good fit.",
  },
  {
    icon: 'ri-map-pin-2-line',
    title: 'Market Availability Check',
    desc: 'We confirm if your area is open — we work with one firm per market.',
  },
  {
    icon: 'ri-arrow-up-circle-line',
    title: 'Clear Growth Plan',
    desc: 'Walk away with a clear strategy to generate more cases.',
  },
];

export default function ConsultationSection() {
  const { ref: sectionRef, isVisible: sectionVisible } = useReveal<HTMLDivElement>(0.1);

  return (
    <section
      id="consultation"
      className="relative py-32 px-6 lg:px-12 overflow-hidden"
      style={{ backgroundColor: '#000000' }}
    >
      {/* Background texture image */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          backgroundImage: `url('https://static.readdy.ai/image/ad76438f27b7c76a25e10aafd906f29a/e4f6ee985377cba9b679ed9e1bdbb64a.png')`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          opacity: 1,
        }}
      />
      {/* Dark overlay */}
      <div className="absolute inset-0 pointer-events-none" style={{ background: 'rgba(0,0,0,0.35)' }} />
      {/* Top divider glow */}
      <div
        className="absolute top-0 left-1/2 -translate-x-1/2 w-[600px] h-[1px] pointer-events-none"
        style={{ background: 'linear-gradient(90deg, transparent, rgba(212,175,55,0.25), transparent)' }}
      />

      <div className="relative z-10 max-w-4xl mx-auto">
        <div
          ref={sectionRef}
          className="text-center"
          style={{
            opacity: sectionVisible ? 1 : 0,
            transform: sectionVisible ? 'translateY(0)' : 'translateY(32px)',
            transition: 'opacity 0.8s ease, transform 0.8s ease',
          }}
        >
          {/* Label */}
          <span
            className="inline-block text-[#D4AF37] text-[10px] font-semibold tracking-[0.35em] uppercase mb-7"
            style={{ fontFamily: 'Inter, sans-serif' }}
          >
            Get Started Now
          </span>

          {/* Headline */}
          <h2
            className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-bold leading-tight mb-7"
            style={{ fontFamily: 'Playfair Display, serif' }}
          >
            <span className="text-white">Ready to </span>
            <span className="text-[#D4AF37]">Add More</span>
            <br />
            <span className="text-[#D4AF37]">Cases</span>
            <span className="text-white"> This Month?</span>
          </h2>

          {/* Subtext */}
          <p
            className="text-white/55 text-base max-w-2xl mx-auto leading-relaxed mb-12"
            style={{ fontFamily: 'Inter, sans-serif' }}
          >
            Schedule a strategy call with our team. We&apos;ll analyze your market, identify
            opportunities, and map out how to generate more qualified cases &mdash; before
            you spend a dollar.
          </p>

          {/* Gold divider line */}
          <div
            className="w-24 h-[1px] mx-auto mb-12"
            style={{ background: 'linear-gradient(90deg, transparent, #D4AF37, transparent)' }}
          />

          {/* CTA Button */}
          <button
            onClick={() => {
              const el = document.getElementById('contact');
              if (el) el.scrollIntoView({ behavior: 'smooth' });
            }}
            className="inline-block px-8 sm:px-14 md:px-20 py-4 md:py-5 bg-[#D4AF37] text-black text-sm font-bold tracking-widest uppercase rounded-md hover:bg-[#e8c547] transition-all duration-300 whitespace-nowrap cursor-pointer mb-8"
            style={{ fontFamily: 'Inter, sans-serif', letterSpacing: '0.18em' }}
          >
            Schedule Your Strategy Call
          </button>

          {/* Trust badges */}
          <div
            className="flex flex-wrap items-center justify-center gap-x-3 gap-y-2 text-white/40 text-sm mb-16"
            style={{ fontFamily: 'Inter, sans-serif' }}
          >
            <span>No long-term contracts</span>
            <span className="text-[#D4AF37]">&bull;</span>
            <span>Exclusive leads</span>
            <span className="text-[#D4AF37]">&bull;</span>
            <span>Built for PI firms</span>
          </div>

          {/* Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {cards.map((card, i) => (
              <div
                key={i}
                className="rounded-lg p-7 text-left hover:border-[#D4AF37]/25 transition-all duration-400 border border-white/[0.08]"
                style={{
                  background: 'rgba(20,14,8,0.7)',
                  backdropFilter: 'blur(6px)',
                  opacity: sectionVisible ? 1 : 0,
                  transform: sectionVisible ? 'translateY(0)' : 'translateY(20px)',
                  transition: `opacity 0.6s ease ${0.35 + i * 0.12}s, transform 0.6s ease ${0.35 + i * 0.12}s, border-color 0.3s ease`,
                }}
              >
                <div className="w-10 h-10 flex items-center justify-center mb-5">
                  <i className={`${card.icon} text-xl text-[#D4AF37]`} />
                </div>
                <h3
                  className="text-base font-bold text-white mb-3"
                  style={{ fontFamily: 'Inter, sans-serif' }}
                >
                  {card.title}
                </h3>
                <p
                  className="text-white/45 text-sm leading-relaxed"
                  style={{ fontFamily: 'Inter, sans-serif' }}
                >
                  {card.desc}
                </p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
